import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X, Star, Image as ImageIcon } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ABOVE_FOLD_COUNT = 3;

function absUrl(u) {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  if (u.startsWith('/')) return `${BACKEND}${u}`;
  return u;
}

function SortableItem({ url, index, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: url + '|' + index });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
  };
  const aboveFold = index < ABOVE_FOLD_COUNT;
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`gallery-item-${index}`}
      className={`relative group rounded-lg overflow-hidden border ${aboveFold ? 'border-amber-500/60 ring-2 ring-amber-500/20' : 'border-zinc-700'} bg-zinc-900`}
    >
      <div className="aspect-[4/3] bg-zinc-800">
        <img src={absUrl(url)} alt="" className="w-full h-full object-cover" loading="lazy" />
      </div>
      {aboveFold && (
        <div className="absolute top-1.5 left-1.5 flex items-center gap-1 px-2 py-0.5 bg-amber-500 text-zinc-900 rounded-full text-[10px] font-bold uppercase tracking-wider">
          <Star size={10} fill="currentColor" /> #{index + 1}
        </div>
      )}
      <button
        type="button"
        onClick={() => onRemove(index)}
        data-testid={`gallery-remove-${index}`}
        className="absolute top-1.5 right-1.5 p-1 bg-red-600/90 hover:bg-red-500 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
        title="Ukloni"
      >
        <X size={12} />
      </button>
      <button
        type="button"
        {...attributes}
        {...listeners}
        data-testid={`gallery-drag-${index}`}
        className="absolute bottom-1.5 left-1.5 p-1.5 bg-zinc-950/80 hover:bg-zinc-900 rounded cursor-grab active:cursor-grabbing text-zinc-300 touch-none"
        title="Povuci za preslagivanje"
      >
        <GripVertical size={14} />
      </button>
      <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 bg-zinc-950/80 text-[10px] font-mono text-zinc-400 rounded">
        {index + 1}
      </div>
    </div>
  );
}

export default function SortableGallery({ urls = [], onChange, onPickMedia }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  function handleDragEnd(e) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = urls.map((u, i) => u + '|' + i);
    const oldIdx = ids.indexOf(active.id);
    const newIdx = ids.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    onChange(arrayMove(urls, oldIdx, newIdx));
  }

  function remove(i) {
    const next = urls.filter((_, j) => j !== i);
    onChange(next);
  }

  return (
    <div data-testid="sortable-gallery">
      <div className="flex items-center justify-between mb-2 text-[11px] text-zinc-500">
        <span>
          Povucite slike za preslagivanje. Prve {ABOVE_FOLD_COUNT} su <strong className="text-amber-400">iznad sloma</strong> (above the fold).
        </span>
        <span className="font-mono">{urls.length} slika</span>
      </div>
      {urls.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={urls.map((u, i) => u + '|' + i)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {urls.map((url, i) => (
                <SortableItem key={url + '|' + i} url={url} index={i} onRemove={remove} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      <div className="flex gap-2 mt-3">
        <button
          type="button"
          onClick={onPickMedia}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs rounded-md border border-zinc-700"
          data-testid="add-gallery-btn"
        >
          <ImageIcon size={13} /> Dodaj iz medija
        </button>
      </div>
    </div>
  );
}
